export type DateTimePickerHourFormat = '12' | '24';

export type DateTimePickerTimeParts = {
  hour: number;
  minute: number;
  pm: boolean;
};

export function readTimePartsFromDate(
  value: Date | null | undefined,
  hourFormat: DateTimePickerHourFormat,
): DateTimePickerTimeParts {
  if (value == null || Number.isNaN(value.getTime())) {
    return hourFormat === '24'
      ? { hour: 0, minute: 0, pm: false }
      : { hour: 12, minute: 0, pm: false };
  }

  const minute = value.getMinutes();
  if (hourFormat === '24') {
    return {
      hour: value.getHours(),
      minute,
      pm: value.getHours() >= 12,
    };
  }

  const hours24 = value.getHours();
  let hour12 = hours24 % 12;
  if (hour12 === 0) {
    hour12 = 12;
  }

  return {
    hour: hour12,
    minute,
    pm: hours24 >= 12,
  };
}

export function hour12To24(hour12: number, pm: boolean): number {
  const h = Math.min(12, Math.max(1, Math.round(hour12)));
  if (h === 12) {
    return pm ? 12 : 0;
  }
  return pm ? h + 12 : h;
}

export function clampMinute(minute: number): number {
  return Math.min(59, Math.max(0, Math.round(minute)));
}

export function clampHourForFormat(
  hour: number,
  hourFormat: DateTimePickerHourFormat,
): number {
  if (hourFormat === '24') {
    return Math.min(23, Math.max(0, Math.round(hour)));
  }
  return Math.min(12, Math.max(1, Math.round(hour)));
}

/**
 * Merge calendar date with manual time parts (local timezone).
 */
export function applyTimePartsToDate(
  base: Date | null | undefined,
  parts: DateTimePickerTimeParts,
  hourFormat: DateTimePickerHourFormat,
): Date | null {
  const anchor =
    base != null && !Number.isNaN(base.getTime()) ? new Date(base) : new Date();
  const minute = clampMinute(parts.minute);
  const hour24 =
    hourFormat === '24'
      ? clampHourForFormat(parts.hour, '24')
      : hour12To24(parts.hour, parts.pm);

  anchor.setHours(hour24, minute, 0, 0);
  return anchor;
}

export function sameDateInstant(
  a: Date | null | undefined,
  b: Date | null | undefined,
): boolean {
  if (a == null && b == null) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  return a.getTime() === b.getTime();
}
